'use client';
import * as React from 'react';
import invariant from 'invariant';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

import { CrudContext } from '../shared/context';
import { useActivePage } from '../useActivePage';
import { useNotifications } from '../useNotifications';
import { useLocaleText } from '../AppProvider/LocalizationProvider';
import { PageContainer, type PageContainerProps } from '../PageContainer';

import { DataSourceCache } from './cache';
import { useFormValidation } from './useFormValidation';
import { useCachedDataSource } from './useCachedDataSource';
import { CrudForm, CrudFormSlots, CrudFormSlotProps } from './CrudForm';
import { type CRUDLocaleText, CRUD_DEFAULT_LOCALE_TEXT } from './localeText';
import {
  OmitId,
  DataModel,
  DataSource,
  DataModelId,
  DataFieldFormValue,
} from './types';

interface EditFormProps<D extends DataModel> {
  localeText: CRUDLocaleText;
  initialValues: Partial<OmitId<D>>;
  slots?: {
    form?: CrudFormSlots;
  };
  slotProps?: {
    form?: CrudFormSlotProps;
  };
  onSubmit: (formValues: Partial<OmitId<D>>) => Promise<void> | void;
  onSubmitSuccess?: (formValues: Partial<OmitId<D>>) => Promise<void> | void;
  dataSource: DataSource<D> &
    Required<Pick<DataSource<D>, 'getOne' | 'updateOne'>>;
}

function EditForm<D extends DataModel>(props: EditFormProps<D>) {
  const {
    slots,
    onSubmit,
    slotProps,
    dataSource,
    localeText,
    initialValues,
    onSubmitSuccess,
  } = props;
  const { fields, validate } = dataSource;

  const notifications = useNotifications();

  const [formState, setFormState] = React.useState<{
    values: Partial<OmitId<D>>;
    errors: Partial<Record<keyof D, string>>;
  }>({
    errors: {},
    values: {
      ...Object.fromEntries(
        fields
          .filter(({ field, editable }) => field !== 'id' && editable !== false)
          .map(({ type, field }) => [
            field,
            type === 'boolean'
              ? (initialValues[field] ?? false)
              : initialValues[field],
          ])
      ),
      ...initialValues,
    },
  });
  const formValues = formState.values;
  const { validateForm, validateField, resetValidation } = useFormValidation<D>(
    { fields, validate },
    setFormState
  );

  const setFormValues = React.useCallback(
    (newFormValues: Partial<OmitId<D>>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    []
  );

  const setFormErrors = React.useCallback(
    (newFormErrors: Partial<Record<keyof D, string>>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    []
  );

  const handleFormFieldChange = React.useCallback(
    (
      name: keyof D,
      value: DataFieldFormValue,
      updates?: Record<string, DataFieldFormValue>
    ) => {
      const newFormValues = { ...formValues, ...updates, [name]: value };

      setFormValues(newFormValues);
      void validateField(name, newFormValues);
    },
    [formValues, setFormValues, validateField]
  );

  const handleFormReset = React.useCallback(() => {
    resetValidation();
    setFormValues(initialValues);
    setFormErrors({});
  }, [initialValues, setFormValues, setFormErrors, resetValidation]);

  const handleFormSubmit = React.useCallback(async () => {
    if (!(await validateForm(formValues))) return;

    try {
      await onSubmit(formValues);
      notifications.show(localeText.editSuccessMessage, {
        severity: 'success',
        autoHideDuration: 3000,
      });

      if (onSubmitSuccess) {
        await onSubmitSuccess(formValues);
      }
    } catch (editError) {
      notifications.show(
        `${localeText.editErrorMessage} ${(editError as Error).message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        }
      );
    }
  }, [
    formValues,
    localeText.editErrorMessage,
    localeText.editSuccessMessage,
    notifications,
    onSubmit,
    onSubmitSuccess,
    validateForm,
  ]);

  return (
    <CrudForm
      slots={slots?.form}
      formState={formState}
      dataSource={dataSource}
      onReset={handleFormReset}
      onSubmit={handleFormSubmit}
      slotProps={slotProps?.form}
      submitButtonLabel={localeText.save}
      onFieldChange={handleFormFieldChange}
    />
  );
}

EditForm.propTypes /* remove-proptypes */ = {
  onSubmitSuccess: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  // ┌────────────────────────────── Warning ──────────────────────────────┐
  // │ These PropTypes are generated from the TypeScript type definitions. │
  // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
  // └─────────────────────────────────────────────────────────────────────┘
  dataSource: PropTypes.object.isRequired,
  localeText: PropTypes.object.isRequired,
  initialValues: PropTypes.object.isRequired,
  slotProps: PropTypes.shape({
    form: PropTypes.shape({
      select: PropTypes.object,
      checkbox: PropTypes.object,
      textField: PropTypes.object,
      datePicker: PropTypes.object,
      dateTimePicker: PropTypes.object,
    }),
  }),
  slots: PropTypes.shape({
    form: PropTypes.shape({
      select: PropTypes.elementType,
      checkbox: PropTypes.elementType,
      textField: PropTypes.elementType,
      datePicker: PropTypes.elementType,
      dateTimePicker: PropTypes.elementType,
    }),
  }),
} as any;

export interface EditProps<D extends DataModel> {
  id: DataModelId;
  /**
   * The title of the page.
   */
  pageTitle?: string;
  /**
   * Locale text for the component.
   */
  localeText?: CRUDLocaleText;
  /**
   * [Cache](https://mui.com/toolpad/core/react-crud/#data-caching) for the data source.
   */
  dataSourceCache?: DataSourceCache | null;
  /**
   * Callback fired when the form is successfully submitted.
   */
  onSubmitSuccess?: (formValues: Partial<OmitId<D>>) => Promise<void> | void;
  /**
   * Server-side [data source](https://mui.com/toolpad/core/react-crud/#data-sources).
   */
  dataSource?: DataSource<D> &
    Required<Pick<DataSource<D>, 'getOne' | 'updateOne'>>;
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps?: {
    form?: CrudFormSlotProps;
    pageContainer?: PageContainerProps;
  };
  /**
   * The components used for each slot inside.
   * @default {}
   */
  slots?: {
    form?: CrudFormSlots;
    pageContainer?: React.JSXElementConstructor<PageContainerProps>;
  };
}

/**
 *
 * Demos:
 *
 * - [CRUD](https://mui.com/toolpad/core/react-crud/)
 *
 * API:
 *
 * - [Edit API](https://mui.com/toolpad/core/api/edit)
 */
function Edit<D extends DataModel>(props: EditProps<D>) {
  const {
    id,
    slots,
    pageTitle,
    slotProps,
    onSubmitSuccess,
    dataSourceCache,
    localeText: propsLocaleText,
  } = props;

  const globalLocaleText = useLocaleText();

  const crudContext = React.useContext(CrudContext);
  const dataSource = (props.dataSource ??
    crudContext.dataSource) as NonNullable<typeof props.dataSource>;

  invariant(dataSource, 'No data source found.');

  const cache = React.useMemo(() => {
    const manualCache = dataSourceCache ?? crudContext.dataSourceCache;
    return typeof manualCache !== 'undefined'
      ? manualCache
      : new DataSourceCache();
  }, [crudContext.dataSourceCache, dataSourceCache]);
  const cachedDataSource = useCachedDataSource<D>(
    dataSource,
    cache
  ) as NonNullable<typeof props.dataSource>;

  const { fields, validate, ...methods } = cachedDataSource;
  const { getOne, updateOne } = methods;

  const activePage = useActivePage();

  const cachedData = React.useMemo(
    () => cache && (cache.get(JSON.stringify(['getOne', id])) as D),
    [cache, id]
  );

  const [data, setData] = React.useState<D | null>(cachedData);
  const [isLoading, setIsLoading] = React.useState(!cachedData);
  const [error, setError] = React.useState<Error | null>(null);

  const loadData = React.useCallback(async () => {
    setError(null);

    let showData = cachedData;
    if (!showData) {
      setIsLoading(true);

      try {
        showData = await getOne(id);
      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }

    if (showData) {
      setData(showData);
    }
    setIsLoading(false);
  }, [cachedData, getOne, id]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = React.useCallback(
    async (formValues: Partial<OmitId<D>>) => {
      const updatedData = await updateOne(id, formValues);
      setData(updatedData);
    },
    [id, updateOne]
  );

  const renderEdit = React.useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            m: 1,
            flex: 1,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    const localeText = {
      ...CRUD_DEFAULT_LOCALE_TEXT,
      ...globalLocaleText,
      ...propsLocaleText,
    };

    return data ? (
      <EditForm
        slots={slots}
        initialValues={data}
        slotProps={slotProps}
        dataSource={dataSource}
        onSubmit={handleSubmit}
        localeText={localeText}
        onSubmitSuccess={onSubmitSuccess}
      />
    ) : null;
  }, [
    data,
    dataSource,
    error,
    globalLocaleText,
    handleSubmit,
    isLoading,
    onSubmitSuccess,
    propsLocaleText,
    slotProps,
    slots,
  ]);

  const PageContainerSlot = slots?.pageContainer ?? PageContainer;

  return (
    <PageContainerSlot
      title={pageTitle}
      breadcrumbs={
        activePage && pageTitle
          ? [
              ...activePage.breadcrumbs,
              {
                title: pageTitle,
              },
            ]
          : undefined
      }
      {...slotProps?.pageContainer}
    >
      <Box sx={{ flex: 1, display: 'flex' }}>{renderEdit}</Box>
    </PageContainerSlot>
  );
}

Edit.propTypes /* remove-proptypes */ = {
  /**
   * The title of the page.
   */
  pageTitle: PropTypes.string,
  // ┌────────────────────────────── Warning ──────────────────────────────┐
  // │ These PropTypes are generated from the TypeScript type definitions. │
  // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
  // └─────────────────────────────────────────────────────────────────────┘
  /**
   * Server-side [data source](https://mui.com/toolpad/core/react-crud/#data-sources).
   */
  dataSource: PropTypes.object,
  /**
   * Locale text for the component.
   */
  localeText: PropTypes.object,
  /**
   * Callback fired when the form is successfully submitted.
   */
  onSubmitSuccess: PropTypes.func,
  /**
   * @ignore
   */
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  /**
   * [Cache](https://mui.com/toolpad/core/react-crud/#data-caching) for the data source.
   */
  dataSourceCache: PropTypes.shape({
    get: PropTypes.func.isRequired,
    set: PropTypes.func.isRequired,
    clear: PropTypes.func.isRequired,
    ttl: PropTypes.number.isRequired,
    cache: PropTypes.object.isRequired,
  }),
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps: PropTypes.shape({
    pageContainer: PropTypes.object,
    form: PropTypes.shape({
      select: PropTypes.object,
      checkbox: PropTypes.object,
      textField: PropTypes.object,
      datePicker: PropTypes.object,
      dateTimePicker: PropTypes.object,
    }),
  }),
  /**
   * The components used for each slot inside.
   * @default {}
   */
  slots: PropTypes.shape({
    pageContainer: PropTypes.elementType,
    form: PropTypes.shape({
      select: PropTypes.elementType,
      checkbox: PropTypes.elementType,
      textField: PropTypes.elementType,
      datePicker: PropTypes.elementType,
      dateTimePicker: PropTypes.elementType,
    }),
  }),
} as any;

export { Edit };
